import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { getTokenMetadata } from "@solana/spl-token";
import { clusterApiUrl, Connection, Keypair, PublicKey } from "@solana/web3.js";
import { parseAdditionalMetadata } from "../../../shared/src/Functions/Utils.ts";
import {
    AvatarContainer,
    AvatarError,
    AvatarIMG,
    AvatarSkeleton,
    BackgroundIMG,
    BackgroundSkeleton,
    HeaderContainer,
} from "../../../shared/src/Components/Service.ts";
import { BackgroundFiller } from "../../../Extension/src/Pages/ServiceAndTariff/ServicePage.tsx";
import styled from "styled-components";
import { VCenter, VStack } from "../../../shared/src/Components/Stacks.ts";
import { ServiceAdditionalMetadata, TariffAdditionalMetadata } from "../../../shared/src/Types/Types.ts";
import { AnchorProvider, Program } from "@coral-xyz/anchor";
import { idl_json } from "../../../Extension/Constants.ts";
import TariffCard from "../Components/TariffCard.tsx";
import { WalletName } from "@solana/wallet-adapter-base";
import { Wallet } from "@solana/wallet-adapter-react";
import { useWalletMultiButton } from "@solana/wallet-adapter-base-ui";

import { Comment, GigaTitle, TextSkeleton } from "../../../shared/src/Components/Typography.ts";
import Header from "../Components/Header.tsx";
import { SelectWalletButton } from "../Components/SelectWalletButton.tsx";
import { CoolProgram } from "../../../backend/target/types/cool_program.ts";

const Wrapper = styled.div`
    display: flex;
    max-width: 600px;
    flex-grow: 1;
    flex-direction: column;
`;

const Grid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); /* Min 1 column, max 2 */
    gap: 8px 16px; /* 8px vertical gap, 16px horizontal gap */
    width: 100%; /* Stretches to parent */
    padding: var(--spacing-12);
`;

// async function fetchTariffs() {}

const Service = () => {
    const { mintAddress } = useParams();

    const [service, setService] = useState<ServiceAdditionalMetadata | undefined>(undefined);
    const [tariffs, setTariffs] = useState<TariffAdditionalMetadata[] | undefined>(undefined);

    useEffect(() => {
        async function getMetadata() {
            console.log(mintAddress);
            const connection = new Connection(clusterApiUrl("devnet"), "confirmed");

            if (!mintAddress) {
                throw new Error("Mint address is undefined");
            }
            const pubKey = new PublicKey(mintAddress);
            const metadata = await getTokenMetadata(connection, pubKey);

            if (!metadata) {
                throw new Error(`Failed to get metadata from ${pubKey}`);
            }
            const additionalMetadata = parseAdditionalMetadata(
                metadata.additionalMetadata
            ) as ServiceAdditionalMetadata;
            console.log("service: ", additionalMetadata);
            setService(additionalMetadata);

            const dummyWallet = {
                publicKey: Keypair.generate().publicKey, // Fake public key, never used
                signTransaction: async (tx: any) => tx, // No signing needed
                signAllTransactions: async (txs: any) => txs,
            };
            const provider = new AnchorProvider(connection, dummyWallet, {
                preflightCommitment: "processed",
            });
            const program = new Program<CoolProgram>(idl_json, provider);
            const tariffs = await program.account.serviceTariffList.fetch(
                new PublicKey(additionalMetadata.tariffs_list)
            );
            console.log("tariffs");
            console.log(tariffs);
            const tariffs_metadata: TariffAdditionalMetadata[] = [];
            for (const tariff of tariffs.tariffs) {
                const tariff_metadata = await getTokenMetadata(connection, tariff);
                console.log("tariff_metadata:", tariff_metadata);
                if (!tariff_metadata) {
                    throw new Error(`Failed to get tariff metadata from ${tariff}`);
                }
                const tariff_additionalMetadata = parseAdditionalMetadata(
                    tariff_metadata.additionalMetadata
                ) as TariffAdditionalMetadata;
                console.log("tariff_additionalMetadata:", tariff_additionalMetadata);
                tariffs_metadata.push(tariff_additionalMetadata);
                tariffs_metadata.push(tariff_additionalMetadata);
                tariffs_metadata.push(tariff_additionalMetadata);
            }
            setTariffs(tariffs_metadata);
        }

        getMetadata();
    }, [mintAddress]);

    const [isBackgroundLoaded, setIsBackgroundLoaded] = useState(false);
    const [backgroundImageError, setBackgroundImageError] = useState(false);
    const [avatarImageError, setAvatarImageError] = useState(false);
    const [isAvatarLoaded, setIsAvatarLoaded] = useState(false);

    const [walletModalConfig, setWalletModalConfig] = useState<Readonly<{
        onSelectWallet(walletName: WalletName): void;
        wallets: Wallet[];
    }> | null>(null);

    const [hasSubscryptoInstalled, setHasSubscryptoInstalled] = useState<boolean | undefined>(undefined);
    const { buttonState, onConnect, onDisconnect, onSelectWallet } = useWalletMultiButton({
        onSelectWallet: setWalletModalConfig,
    });

    useEffect(() => {
        if (walletModalConfig === null && onSelectWallet) {
            onSelectWallet();
        }
        if (walletModalConfig) {
            console.warn("walletModalConfig");
            console.warn(walletModalConfig);
            const subscryptoWalletAdapter = walletModalConfig.wallets.find(
                (wallet) => wallet.adapter.name === "Subscrypto"
            );
            if (subscryptoWalletAdapter) {
                console.log("found Subscrypto wallet");
                setHasSubscryptoInstalled(true);
            } else {
                console.warn("no Subscrypto wallet found");
                setHasSubscryptoInstalled(false);
            }
        }
    }, [walletModalConfig, onSelectWallet]);

    function handleBackgroundError() {
        setBackgroundImageError(true);
    }

    function handleAvatarError() {
        setAvatarImageError(true);
    }

    return (
        <Wrapper>
            <Header>
                <SelectWalletButton
                    walletModalConfig={walletModalConfig}
                    buttonState={buttonState}
                    onConnect={onConnect}
                    onDisconnect={onDisconnect}
                    hasSubscryptoInstalled={hasSubscryptoInstalled}
                />
            </Header>
            <button
                onClick={() => {
                    console.log("walletModalConfig", walletModalConfig);
                }}
            >
                click
            </button>
            <VStack gap={"18"}>
                <HeaderContainer>
                    {backgroundImageError ? (
                        <BackgroundFiller />
                    ) : (
                        <>
                            {!isBackgroundLoaded && <BackgroundSkeleton />}
                            {service && (
                                <BackgroundIMG
                                    src={service.background_link}
                                    alt="background"
                                    onLoad={() => setIsBackgroundLoaded(true)}
                                    onError={handleBackgroundError}
                                    style={{
                                        display: isBackgroundLoaded ? "block" : "none",
                                    }} // Hide until loaded
                                />
                            )}
                        </>
                    )}
                    <AvatarContainer>
                        {service ? (
                            avatarImageError ? (
                                <AvatarError>
                                    <h3>Error</h3>
                                </AvatarError>
                            ) : (
                                <>
                                    {!isAvatarLoaded && <AvatarSkeleton />}
                                    <AvatarIMG
                                        src={service.logo_link}
                                        alt="avatar"
                                        onLoad={() => setIsAvatarLoaded(true)}
                                        onError={handleAvatarError}
                                        style={{
                                            display: isAvatarLoaded ? "block" : "none",
                                        }} // Hide until loaded
                                    />
                                </>
                            )
                        ) : (
                            <AvatarSkeleton />
                        )}
                    </AvatarContainer>
                </HeaderContainer>
                {service ? (
                    <VStack gap={"12"} padding_sides={"24"}>
                        <GigaTitle>{service.title}</GigaTitle>
                        <p>{service.description}</p>
                    </VStack>
                ) : (
                    <VStack gap={"12"}>
                        <TextSkeleton variant={"giga"} width_percentage={60} />
                        <TextSkeleton variant={"p"} width_percentage={100} />
                        <TextSkeleton variant={"p"} width_percentage={100} />
                        <TextSkeleton variant={"p"} width_percentage={50} />
                    </VStack>
                )}

                <Grid>
                    {tariffs === undefined ? (
                        <>
                            <TariffCard isLoading={true} />
                            <TariffCard isLoading={true} />
                            <TariffCard isLoading={true} />
                            <TariffCard isLoading={true} />
                        </>
                    ) : tariffs.length > 0 ? (
                        tariffs.map((tariff, index) => (
                            <TariffCard
                                key={index}
                                tariff={tariff}
                                hasSubscryptoInstalled={hasSubscryptoInstalled}
                                hasSubscryptoConnected={buttonState === "connected"}
                            />
                        ))
                    ) : (
                        <VCenter>
                            <Comment>The service does no have any tariffs</Comment>
                        </VCenter>
                    )}
                </Grid>
            </VStack>
        </Wrapper>
    );
};

export default Service;
